import { Table } from '@tanstack/react-table';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { CheckedState } from '@radix-ui/react-checkbox';

type SelectionType = 'page' | 'all' | 'none' | null;

interface SelectAllCheckboxProps {
  table: Table<Client>;
  defaultSelection?: SelectionType;
  onSelectionChange?: (type: SelectionType) => void;
}

const getTableState = (table: Table<Client>) => ({
  allPageRowsSelected: table.getIsAllPageRowsSelected(),
  somePageRowsSelected: table.getIsSomePageRowsSelected(),
  isAnyFilteredRowSelected: table.getFilteredSelectedRowModel().rows.length > 0,
  isAllFilteredRowsSelected: table.getIsAllRowsSelected(),
});

const calculateDerivedState = (
  table: Table<Client>,
  appliedSelection: SelectionType,
) => {
  const {
    allPageRowsSelected,
    somePageRowsSelected,
    isAnyFilteredRowSelected,
    isAllFilteredRowsSelected,
  } = getTableState(table);

  if (appliedSelection === 'none') {
    return { checkboxState: false as CheckedState };
  }

  const isAllSelected =
    appliedSelection === 'all'
      ? isAllFilteredRowsSelected
      : allPageRowsSelected;
  const isSomeSelected =
    appliedSelection === 'all'
      ? isAnyFilteredRowSelected
      : somePageRowsSelected;

  return {
    checkboxState: (isAllSelected
      ? true
      : isSomeSelected
        ? 'indeterminate'
        : false) as CheckedState,
  };
};

/**
 * Component for select-all checkbox with selection options popover
 * Provides "Select this page", "Select all clients", and "Deselect all" options
 * Defaults to "Select all clients"
 */
export const SelectAllCheckbox = ({
  table,
  defaultSelection = 'all',
  onSelectionChange,
}: SelectAllCheckboxProps) => {
  const [open, setOpen] = useState(false);
  const [selectionType, setSelectionType] =
    useState<SelectionType>(defaultSelection);
  const [appliedSelection, setAppliedSelection] =
    useState<SelectionType>(defaultSelection);
  const [isInitialized, setIsInitialized] = useState(false);

  const [pageRowCount, setPageRowCount] = useState(0);
  const [totalRowCount, setTotalRowCount] = useState(0);

  const previousPageIndex = useRef(table?.getState().pagination.pageIndex);

  const updateRowCounts = useCallback(() => {
    setPageRowCount(table.getRowModel().rows.length || 0);
    setTotalRowCount(table.getFilteredRowModel().rows.length || 0);
  }, [table]);

  const selectAllFilteredRows = useCallback(() => {
    const newSelectionState: Record<string, boolean> = {};
    table.getFilteredRowModel().rows.forEach(row => {
      if (row.original.clientEmail) {
        newSelectionState[row.id] = true;
      }
    });
    table.setRowSelection(newSelectionState);
  }, [table]);

  useEffect(() => {
    if (isInitialized) return;

    try {
      // Only initialize if there's no existing selection
      const hasExistingSelection =
        Object.keys(table.getState().rowSelection).length > 0;
      if (!hasExistingSelection) {
        if (defaultSelection === 'all') {
          selectAllFilteredRows();
        } else if (defaultSelection === 'page') {
          table.toggleAllPageRowsSelected(true);
        } else {
          table.toggleAllRowsSelected(false);
        }
      }
      setIsInitialized(true);

      if (onSelectionChange) {
        onSelectionChange(defaultSelection);
      }
    } catch (error) {
      console.error('Error setting initial selection:', error);
    }
  }, [
    isInitialized,
    selectAllFilteredRows,
    defaultSelection,
    onSelectionChange,
    table,
  ]);

  const currentPageIndexForEffect = useMemo(
    () => table?.getState().pagination.pageIndex,
    [table],
  );

  useEffect(() => {
    if (!isInitialized) return;

    try {
      updateRowCounts();

      const currentPageIndex = table.getState().pagination.pageIndex;
      const previousPage = previousPageIndex.current;
      const isPageChanged =
        previousPage !== undefined && previousPage !== currentPageIndex;

      if (isPageChanged && appliedSelection === 'page') {
        setAppliedSelection(null);
        setSelectionType(null);
        if (onSelectionChange) {
          onSelectionChange(null);
        }
      }

      previousPageIndex.current = currentPageIndex;
    } catch (error) {
      console.error('Error in pagination effect:', error);
    }
  }, [
    isInitialized,
    appliedSelection,
    updateRowCounts,
    onSelectionChange,
    currentPageIndexForEffect,
    table,
  ]);

  const { checkboxState: derivedCheckboxState } = calculateDerivedState(
    table,
    appliedSelection,
  );

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      updateRowCounts();

      const {
        allPageRowsSelected,
        somePageRowsSelected,
        isAllFilteredRowsSelected,
      } = getTableState(table);

      let newSelectionTypeForPopover: SelectionType = null;

      if (appliedSelection === 'all' && isAllFilteredRowsSelected) {
        newSelectionTypeForPopover = 'all';
      } else if (appliedSelection === 'page' && allPageRowsSelected) {
        newSelectionTypeForPopover = 'page';
      } else if (appliedSelection === 'none') {
        newSelectionTypeForPopover = 'none';
      } else if (appliedSelection === null) {
        if (allPageRowsSelected) {
          newSelectionTypeForPopover = 'page';
        } else if (!somePageRowsSelected) {
          newSelectionTypeForPopover = 'none';
        }
      }

      setSelectionType(newSelectionTypeForPopover);
    }
  };

  const handleSelectionTypeChange = (value: string) => {
    setSelectionType(value as SelectionType);
  };

  const handleCheckboxClick = () => setOpen(true);

  const handleSelectionCancel = () => setOpen(false);

  const handleSelectionApply = () => {
    try {
      if (!selectionType) return;

      let newAppliedSelection: SelectionType = appliedSelection;

      if (selectionType === 'page') {
        if (appliedSelection === 'all') {
          table.toggleAllRowsSelected(false);
        }
        const currentPageRowIds = table.getRowModel().rows.reduce(
          (acc, row) => {
            acc[row.id as string] = true;
            return acc;
          },
          {} as Record<string, boolean>,
        );
        table.setRowSelection(currentPageRowIds);
        newAppliedSelection = 'page';
      } else if (selectionType === 'all') {
        selectAllFilteredRows();
        newAppliedSelection = 'all';
      } else if (selectionType === 'none') {
        table.toggleAllRowsSelected(false);
        newAppliedSelection = 'none';
      }

      setAppliedSelection(newAppliedSelection);
      setOpen(false);

      if (onSelectionChange) {
        onSelectionChange(selectionType);
      }
    } catch (error) {
      console.error('Error applying selection:', error);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
    >
      <PopoverTrigger asChild>
        <div className="cursor-pointer">
          <Checkbox
            className="text-white data-[state=checked]:bg-primary"
            checked={derivedCheckboxState}
            onCheckedChange={handleCheckboxClick}
            aria-label="Select options"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-56 p-3"
        align="start"
      >
        <div className="space-y-4">
          <h4 className="font-medium leading-none">Selection Options</h4>
          <RadioGroup
            value={selectionType || ''}
            onValueChange={handleSelectionTypeChange}
            className="gap-3"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value="all"
                id="select-all"
              />
              <Label
                htmlFor="select-all"
                className="flex items-center gap-2"
              >
                Select all clients
                <span className="text-primary-800 inline-flex h-5 items-center rounded-full bg-primary-100 px-2 text-xs font-medium">
                  {totalRowCount}
                </span>
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value="page"
                id="select-page"
              />
              <Label
                htmlFor="select-page"
                className="flex items-center gap-2"
              >
                Select this page
                <span className="text-primary-800 inline-flex h-5 items-center rounded-full bg-primary-100 px-2 text-xs font-medium">
                  {pageRowCount}
                </span>
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value="none"
                id="select-none"
              />
              <Label
                htmlFor="select-none"
                className="flex items-center gap-2"
              >
                Deselect all
              </Label>
            </div>
          </RadioGroup>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectionCancel}
              className="text-xs"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleSelectionApply}
              className="bg-primary text-xs hover:bg-primary-hover"
              disabled={selectionType === null}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
